/* @flow */

import * as React from 'react';
import styled from 'styled-components';
import Calendar from 'react-calendar';

import FlexBox from './FlexBox';
import { Tab } from './MiniSymbolsList';

import withI18n from '../utils/withI18n';
import { pad } from '../utils/number';
import zIndexScale from '../utils/zIndexScale';
import { getCalendarLocale } from '../utils/locales';

const MIN_DATE = '2017-07-14'; // default minimum date

const InputContainer = styled(FlexBox)`
  position: relative;
  .react-calendar {
    background: ${({ theme }) => theme.boxbg};
    border: none;
  }
  .react-calendar__navigation {
    height: 3em;
    button {
      min-width: 14%;
    }
  }
  .react-calendar__navigation button[disabled],
  .react-calendar__month-view button.react-calendar__tile:disabled {
    background-color: ${({ theme }) => theme.bg};
  }
  .react-calendar__month-view__days__day--weekend {
    color: ${({ theme }) => theme.tradingViewStyle.short};
  }
  .react-calendar__tile--active {
    background: ${({ theme }) => theme.boxbg};
    border: 1px solid ${({ theme }) => theme.activeBorder};
    color: ${({ theme }) => theme.highlight};
  }
  .react-calendar__navigation button:enabled:hover,
  .react-calendar__navigation button:enabled:focus,
  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus {
    background-color: ${({ theme }) => theme.inputBg};
  }
`;

const CalendarOuter = styled(FlexBox)`
  background-color: ${({ theme }) => theme.boxaltheadbg};
  border: 1px solid ${({ theme }) => theme.border};
  bottom: 2.2em;
  display: ${({ calendarOpen }) => (calendarOpen ? 'flex' : 'none')};
  left: 0px;
  position: absolute;
  z-index: ${zIndexScale.calendar};
  width: 200%;
`;

const CalendarCTAContainer = styled(FlexBox)`
  padding: .5em;
`;

const TodayTab = styled(Tab)`
  opacity: ${({ disabled }) => disabled ? '.5' : '1'};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
`;

const InputDate = styled.input`
  width: 9em;
  padding-left: .5em;
  height: 22px;
  border: 1px solid ${({ theme }) => theme.inputBorder};
  background-color: #323c45;
  margin: 0 .5em;
  color: #fff;
`;

type Props = {
  symbol: string,
  locale?: string,
  i18n: Object,
  callbackFn: Function,
  passedDate: Date | null,
  minDateStr?: string,
  maxDateStr?: string,
};

type State = {
  date: Date,
  calendarOpen?: boolean,
  formatted?: string,
}

export default
@withI18n
class CalendarInput extends React.PureComponent<Props, State> {
  _node = React.createRef();

  static defaultProps = {
    i18n: {},
    locale: 'en',
    passedDate: new Date(),
    minDateStr: '',
    maxDateStr: '',
  }

  state = { date: new Date(), calendarOpen: false, formatted: '' }

  componentDidMount() {
    window.addEventListener('mousedown', this._onClickedOutside);
    window.addEventListener('touchstart', this._onClickedOutside);
  }

  componentDidUpdate({ symbol }: Props) {
    if (symbol !== this.props.symbol) {
      this._resetDateInput();
    } else if (this.props.passedDate !== this.state.date && this.state.formatted !== '') {
      this._setDate(this.props.passedDate);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('mousedown', this._onClickedOutside);
    window.removeEventListener('touchstart', this._onClickedOutside);
  }

  _setDate = (date: Date | null) => {
    if (date === null) return;
    const Y = date.getFullYear();
    const M = pad(date.getMonth() + 1);
    const D = pad(date.getDate());
    this.setState({ date, formatted: `${Y}-${M}-${D}` });
  }

  _clear = () => {
    this.setState({ date: new Date(), calendarOpen: false, formatted: '' });
    this.props.callbackFn('');
  }

  _today = () => {
    const today = new Date();
    this._setDate(today);
    this.props.callbackFn(today);
  }

  _today = () => {
    const today = new Date();
    this._setDate(today);
    this.props.callbackFn(today);
  }

  _onChange = (date: Date) => {
    this._setDate(date);
    this.props.callbackFn(date);
  }

  _confirm = () => {
    this._setDate(this.state.date);
    this.setState({ calendarOpen: false });
    this.props.callbackFn(this.state.date);
  }

  _toggleCalendar = () => {
    this.setState({ calendarOpen: !this.state.calendarOpen });
  }

  _onClickedOutside = (ev: MouseEvent) => {
    if (!this.state.calendarOpen || !this._node) return;
    if (this._node.current && this._node.current.contains(ev.target)) {
      return;
    }
    this.setState({ calendarOpen: false });
  }

  _handleMinDate = () => {
    const { minDateStr } = this.props;
    if (!minDateStr) return new Date(MIN_DATE);
    if (minDateStr !== '') return new Date(minDateStr);
    return new Date(MIN_DATE);
  }

  _handleMaxDate = () => {
    const { maxDateStr } = this.props;
    if (!maxDateStr) return new Date();
    if (maxDateStr !== '' && new Date(maxDateStr) <= new Date()) {
      return new Date(maxDateStr);
    }
    return new Date();
  }

  _resetDateInput() {
    this.setState({ formatted: '' });
    this.props.callbackFn('');
  }

  render() {
    const {
      i18n,
      locale,
      maxDateStr,
    } = this.props;
    const { date, calendarOpen, formatted } = this.state;
    return (
      <InputContainer ref={this._node}>
        <CalendarOuter calendarOpen={calendarOpen}>
          <Calendar
            onChange={this._onChange}
            value={date}
            locale={getCalendarLocale(locale) || 'en'}
            onClickDay={this._confirm}
            minDate={this._handleMinDate()}
            maxDate={this._handleMaxDate()}
          />
          <CalendarCTAContainer basis="100%" justify="flex-end">
            <Tab onClick={this._clear}>{i18n.clear || 'Clear'}</Tab>
            <TodayTab disabled={maxDateStr && (new Date(maxDateStr) <= new Date())} onClick={this._today}>{i18n.Today || 'Today'}</TodayTab>
            <Tab onClick={this._confirm}>{i18n.Ok || 'OK'}</Tab>
          </CalendarCTAContainer>
        </CalendarOuter>
        <InputDate onFocus={this._toggleCalendar} type="text" value={formatted} />
      </InputContainer>
    );
  }
}
